/**
 * 双向评价的两条纯规则(S-57,决策 Y-51 / Y-54 / Y-55)。
 * **单点定义在这里,服务端与两个端引用** —— 各端各写一份的上限与方向迟早漂移。
 *
 * ═══ 方向(Y-51)═══
 *
 * 只有两个方向:**下单人评飞手** / **飞手评下单人**。评价双方都挂账号;
 * **收件人不参与评价** —— 哪怕实际体验送得好不好的人就是他。理由:评价的主体必须和
 * 订单的当事人是同一组人,否则「谁有资格评」没有边界,一个订单能产生几条对飞手的评价
 * 会变得不确定,平均分就算不清了。
 *
 * 方向的取值 `customer` / `pilot` 说的是**作者是谁**(不是「谁评谁」)——
 * 因为一单里作者与被评对象互为对方,知道作者是谁就知道方向了。
 *
 * ═══ 评价填什么(Y-54)═══
 *
 * 星级 1–5 **必填** + 文字**选填**(上限 200 字,trim 后为空即视为未填)。
 * 不做标签组 —— 标签组要为两个方向各定义一套词表,而词表是甲方的话术,不是工程能定的。
 */

/** 评价作者的身份(Y-51:只有下单人与飞手两个,收件人不在其中) */
export const REVIEW_AUTHOR_ROLE = ['customer', 'pilot'] as const

export type ReviewAuthorRole = (typeof REVIEW_AUTHOR_ROLE)[number]

/**
 * 两个取值的**命名常量**。服务端的方向判据与投影都引用它们,不再手写字符串 ——
 * 一是不让「把评价方向误写成鉴权角色」这类事发生在两处,二是 guards/check-role-source
 * 只认字面量 `'pilot'`(评价方向里的 pilot 与鉴权角色同名、但含义不同),
 * 让那个字面量只住在这个定义文件里,读的人一眼能分清两者。
 */
export const REVIEW_AUTHOR_CUSTOMER: ReviewAuthorRole = 'customer'
export const REVIEW_AUTHOR_PILOT: ReviewAuthorRole = 'pilot'

export function isReviewAuthorRole(x: unknown): x is ReviewAuthorRole {
  return typeof x === 'string' && (REVIEW_AUTHOR_ROLE as readonly string[]).includes(x)
}

/** 给人看的中文名。页面上不该出现 customer / pilot 原始码 */
export const REVIEW_AUTHOR_ROLE_LABELS: Record<ReviewAuthorRole, string> = {
  customer: '下单人',
  pilot: '飞手',
}

/** 认不出来的取值原样返回(与其它标签函数同一形状:不猜一个档位) */
export function reviewAuthorRoleLabel(value: ReviewAuthorRole | string): string {
  return REVIEW_AUTHOR_ROLE_LABELS[value as ReviewAuthorRole] ?? String(value)
}

/** 星级的合法区间(Y-54:1–5,必填) */
export const REVIEW_RATING_MIN = 1
export const REVIEW_RATING_MAX = 5

/** 评价文字的上限(Y-54:200 字,超长拒,不静默截断) */
export const REVIEW_CONTENT_MAX_LENGTH = 200

/** 星级是不是合法的 1–5 整数(0 / 6 / 非整数 / 字符串一律不是) */
export function isValidRating(x: unknown): x is number {
  return typeof x === 'number' && Number.isInteger(x) && x >= REVIEW_RATING_MIN && x <= REVIEW_RATING_MAX
}

/** 评价区要渲染的一条(契约 components.schemas.Review 的展示面) */
export interface ReviewPanelItem {
  authorRole: ReviewAuthorRole
  rating: number
  content: string | null
  createdAt: string
}

/** 评价区的显示状态(S-58)。`mine` / `theirs` 是后端**已经返回给调用方**的那几条之一 */
export interface ReviewPanelState {
  /** 这一页要不要画评价区(只有「完成」之后才画,Y-55) */
  visible: boolean
  /** 我自己写的那条(后端对作者永远可见),没写过为 null */
  mine: ReviewPanelItem | null
  /** 对方写的那条,**只有后端把它返回给我时**才有值(盲评,Y-50 / Y-52) */
  theirs: ReviewPanelItem | null
  /** 要不要给「填星级 + 文字」的入口 */
  canSubmit: boolean
}

/**
 * 评价区的显示状态 —— **纯规则,下单人侧与飞手侧的订单详情页共用一份**(S-58)。
 *
 * ═══ 为什么它是纯函数、而且只写一份 ═══
 *
 * 这一格最容易做错、而且做错了**不报错**的地方是「盲评怎么呈现」(Y-50 / Y-52):
 * 判反了的表现是「先评的一方看到对方那条还没写出来的评价」,而页面照常渲染、接口 200。
 * 两个端各推一次的话,两处的推导迟早分叉,而分叉的两处各自都「看起来正常」。
 * 所以推导只住在这里,两页引用它。
 *
 * ═══ 它只看后端返回了什么,不自己判可见性(Y-50 / Y-52)═══
 *
 * `items` 是 `GET /api/orders/{orderNo}/reviews` 返回给调用方的那一份 —— **已经是过滤过的**。
 * 这里不做任何「这条该不该给我看」的判断:那是服务端的事(review.mapper 的 isReviewVisibleTo),
 * 在前端再判一遍等于有了第二份可见性真相,两份迟早不一致。这里只回答:
 *
 *   · 这一页画不画评价区(只有「完成」之后才画,Y-55);
 *   · 我自己那条在不在(在 = 我已经评过);
 *   · 对方那条在不在(在 = 后端把它返回给我了);
 *   · 还要不要给入口。
 *
 * ═══ 「等对方评价」是如实的等待态,不是「对方还没写」═══
 *
 * 我评过、对方那条**没**返回给我 —— 这有两种可能:对方真的还没写,或对方写了但按盲评
 * 对我不可见。页面**分不出**这两者(后端只给「对我可见」的那一份),所以只能说
 * 「等对方评价」—— 断言「对方还没写」是在说一件我们不知道的事。
 *
 * ═══ canSubmit 的两条关闭理由 ═══
 *
 *   · 我已经评过(mine 非空)—— 一单一个方向最多一条(Y-51),再给入口只会撞 409;
 *   · 我还没评,却已经能看到对方那条(theirs 非空)—— 双方都提交才互相可见,而我还没提交,
 *     所以唯一能让对方的评价出现在我眼前的是**到点单向解锁**;解锁与评价窗口的边界是同一个
 *     (完成时刻 + reviewWindowDays),所以这时我的窗口也已经过了,不再给入口。
 *     (边界那一毫秒的差别不值得为它多一个字段:后端仍会在提交时兜底拒。)
 *
 * 其余情况(完成、我没评、也没看到对方的)给入口 —— **窗口是否还开由后端判**:
 * 前端不拿完成时刻加天数自己算一遍(那是第二份真相,两边的钟与配置会漂移),
 * 过期时后端 409 REVIEW_WINDOW_CLOSED,页面把服务端那句话原样显示。
 *
 * 函数体只写普通 JS(守卫要 new Function 原样求值,类型注解会让它取不出可执行体)。
 */
export function reviewPanelState(
  status: string,
  items: ReviewPanelItem[],
  myRole: ReviewAuthorRole,
): ReviewPanelState {
  if (status !== 'completed') return { visible: false, mine: null, theirs: null, canSubmit: false }
  const mine = items.find((i) => i.authorRole === myRole) || null
  const theirs = items.find((i) => i.authorRole !== myRole) || null
  const canSubmit = mine === null && theirs === null
  return { visible: true, mine: mine, theirs: theirs, canSubmit: canSubmit }
}
